import type { IOrderLineItemDto } from "@lib/ShiOneClient";

type ProductQuickViewItem = Pick<
  IOrderLineItemDto,
  "productDescriptor" | "shiPartNumber" | "productClass"
>;

const SUPPORTED_PRODUCT_CLASSES = ["shicommodity", "shiconfigsystem"];

/** Product classes the order product drawer API accepts, compared case-insensitively. */
export function isSupportedOrderProductClass(
  productClass: string | null | undefined,
): boolean {
  return (
    productClass != null &&
    SUPPORTED_PRODUCT_CLASSES.includes(productClass.toLowerCase())
  );
}

export type ProductQuickViewTarget = {
  productDescriptor: string;
  productClass: "shiconfigsystem" | "shicommodity";
};

// The product drawer only resolves a catalog record from a numeric descriptor plus a supported
// class, so returns null for lines it cannot open (services, fees, non-numeric part numbers).
export function resolveProductQuickViewTarget(
  item: ProductQuickViewItem,
): ProductQuickViewTarget | null {
  const numericDescriptor =
    [item.productDescriptor, item.shiPartNumber]
      .map((value) => (value == null ? null : String(value)))
      .find((value) => value != null && /^[0-9]+$/.test(value)) ?? null;

  const normalizedProductClass = item.productClass?.toLowerCase() ?? "";
  const productClass = normalizedProductClass.includes("config")
    ? "shiconfigsystem"
    : normalizedProductClass.includes("commodity") ||
        normalizedProductClass === "hardware" ||
        (!normalizedProductClass && numericDescriptor)
      ? "shicommodity"
      : null;

  if (!numericDescriptor || !productClass) {
    return null;
  }

  return { productDescriptor: numericDescriptor, productClass };
}

export function canOpenProductQuickView(item: ProductQuickViewItem): boolean {
  return resolveProductQuickViewTarget(item) != null;
}
